import i18next from 'i18next';
import _ from 'lodash';
import { STRING_UOM } from '@/main/app.constants';
import { defaultNumberFormat } from '@/services/systemConfiguration.utilities';
import { parseEnumerationString } from '@/utilities/enumerationString.utilities';
import { NO_FORMAT, formatNumber } from '@/utilities/numberHelper.utilities';
import { formatString } from '@/utilities/stringHelper.utilities';
import type { FormatOptions } from '@/utilities/numberHelper.utilities';

type ColumnValue = string | number | null | undefined;

interface ColumnSeries {
  formatOptions?: FormatOptions;
}

export interface ColumnDefinition {
  key?: string;
  uomKey?: string;
  formatOptions?: FormatOptions;
  format?: string;
  style?: string;
  isStringCompatible?: boolean;
  accessor?: string;
  series?: ColumnSeries | null;
}

type ColumnItem = {
  startTime?: number | string | null;
  endTime?: number | string | null;
  alias?: string;
  formatOptions?: FormatOptions;
  name?: string;
  lane?: number;
  properties?: Record<string, string | number | boolean | null | undefined>;
  propertiesUOM?: Record<string, string | number | null | undefined>;
  statistics?: Record<string, string | number | null | undefined>;
};

/**
 * Gets a custom property from an item using said propertys column key.
 *
 * @param item - The item object containing data displayed in the column
 * @param columnKey - key to match to object property, column.key or column.uomKey
 *
 * @return The property of `item` that the key matched to
 */
const getUsingKey = (item: ColumnItem, columnKey: string | undefined): ColumnValue => {
  if (!columnKey) {
    return undefined;
  }

  const [parentPropName, ...rest] = _.split(columnKey, '.');

  return _.get(item, _.chain([parentPropName]).concat(rest.join('.')).compact().value()) as ColumnValue;
};

/**
 * Get the value and UOM of a column for an item
 *
 * @param {Object} column - One of the columns from COLUMNS
 * @param {Object} item - Item for which to get a value
 * @param {Function} valueFormatter - Optional function to format the value
 *
 * @returns {String} Formatted value
 */
export function getColumnValueAndUOM(
  column: ColumnDefinition,
  item: ColumnItem,
  valueFormatter: (value: string) => string = _.identity,
): string {
  const isStartTime = column.key === 'startTime';
  const isEndTime = column.key === 'endTime';
  const isName = column.key === 'name';

  if (isStartTime && _.isNil(item.startTime)) {
    return i18next.t('CAPSULES_PANEL.STARTS_OFF_SCREEN');
  }

  if (isEndTime && _.isNil(item.endTime)) {
    return i18next.t('CAPSULES_PANEL.ENDS_OFF_SCREEN');
  }

  const rawFormatOptions: FormatOptions =
    column.formatOptions ?? item.formatOptions ?? column.series?.formatOptions ?? {};
  const formatOptions: FormatOptions = {
    ...rawFormatOptions,
    format: column.format || rawFormatOptions.format,
    isPercent: column.style === 'percent',
  };

  let rawValue: ColumnValue;
  const toString = (value: ColumnValue): string => {
    if (_.isString(value)) {
      const { isEnumString, enumName } = parseEnumerationString(value);
      if (isEnumString) {
        return enumName;
      } else if (column.key?.includes('properties')) {
        return formatString(rawValue as string, {}, _.noop, getDefaultFormat(true));
      } else {
        return rawValue as string;
      }
    } else if (_.isNumber(value)) {
      return _.toString(value);
    }

    return '';
  };

  const rawValueFromAccessor = column.accessor ? (_.get(item, column.accessor) as ColumnValue) : undefined;
  rawValue = rawValueFromAccessor ?? getUsingKey(item, column.key);
  const alias = _.get(item, 'alias');
  if (isName && alias) {
    rawValue = alias;
  }
  const value: string =
    getUsingKey(item, column.uomKey) === STRING_UOM ||
    column.style === STRING_UOM ||
    (column.isStringCompatible && _.isString(rawValue))
      ? toString(rawValue)
      : formatNumber(getUsingKey(item, column.key), formatOptions, _.noop);

  const uom = getUsingKey(item, column.uomKey);

  return `${_.isNil(value) ? '' : valueFormatter(value)}${_.isNil(uom) || uom === STRING_UOM ? '' : uom}`;
}

export function getDefaultFormat(isString: boolean): string {
  return isString ? NO_FORMAT : defaultNumberFormat();
}

export function isValueColumn(column: Partial<ColumnDefinition>): boolean {
  return (
    column.style !== 'icon' &&
    column.style !== 'select' &&
    column.style !== 'input' &&
    column.style !== 'iconSelect' &&
    column.style !== 'assets' &&
    column.style !== 'datasourceName' &&
    column.key !== 'valueUnitOfMeasure'
  );
}
